import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { DashboardOptionsResponse, DashboardSummary, RenderedDashboard } from '../../gen/openapi';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';
import type { ControlSelections } from '../types/controls';

export const useDashboardTemplates = (): UseQueryResult<DashboardSummary[], Error> => {
  const { isAuthenticated } = useAuth();
  const { dashboards } = useApiClient();
  return useQuery<DashboardSummary[], Error>({
    queryKey: ['dashboards', 'templates', 'summary'],
    queryFn: () => dashboards.listTemplatesApiV1DashboardsTemplatesGet({ view: 'summary' }),
    enabled: isAuthenticated,
    staleTime: 60000,
    gcTime: 300000,
  });
};

/**
 * Selectable values for each control, resolved live against the caller's *staged*
 * selections so cascading (`dependsOn`) options update as parents change — before the
 * heavier render is applied. Cheap enough to refetch on every staged change.
 */
export const useControlOptions = (
  templateId: string | undefined,
  selections: ControlSelections,
): UseQueryResult<DashboardOptionsResponse, Error> => {
  const { isAuthenticated } = useAuth();
  const { dashboards } = useApiClient();
  return useQuery<DashboardOptionsResponse, Error>({
    queryKey: ['dashboards', 'options', templateId, selections],
    queryFn: () =>
      dashboards.resolveControlOptionsApiV1DashboardsTemplatesTemplateIdOptionsPost({
        templateId: templateId as string,
        dashboardOptionsRequest: { selections },
      }),
    enabled: isAuthenticated && !!templateId,
    // Keep the previous options while a cascade refetch is in flight, so controls don't blank out.
    placeholderData: keepPreviousData,
    staleTime: 60000,
    gcTime: 300000,
  });
};

/**
 * Renders a dashboard against the *applied* filters. Keyed on them, so it only refetches
 * when the user applies (clicks Refresh) — not on every staged change. `selections` is the
 * single filter map, instance picker included; empty means the caller's full accessible
 * scope (backend default).
 *
 * `enabled` lets the caller hold the render back — e.g. while no deployment is selected, so an
 * unscoped (all-instances) dashboard is never fetched behind the "pick a deployment" prompt.
 */
export const useRenderedDashboard = (
  templateId: string | undefined,
  selections: ControlSelections,
  timeRange?: string,
  enabled = true,
): UseQueryResult<RenderedDashboard, Error> => {
  const { isAuthenticated } = useAuth();
  const { dashboards } = useApiClient();
  return useQuery<RenderedDashboard, Error>({
    queryKey: ['dashboards', 'render', templateId, selections, timeRange ?? null],
    queryFn: () =>
      dashboards.renderTemplateApiV1DashboardsTemplatesTemplateIdRenderPost({
        templateId: templateId as string,
        dashboardRenderRequest: { selections, timeRange },
      }),
    enabled: enabled && isAuthenticated && !!templateId,
    // Keep the previous render (and its controls) on screen while applying new filters, so the
    // filter toolbar stays stable instead of collapsing when the query key changes on Refresh.
    placeholderData: keepPreviousData,
    staleTime: 60000,
    gcTime: 300000,
  });
};
